import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { NAV, AUTH } from "@/constants/testIds";
import { LayoutGrid, TrendingUp, BookOpen, GraduationCap, Settings, ShieldAlert, LogOut, ScrollText, Zap, Sparkles, PieChart, History, Menu, X, Activity, Radar, Brain } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import InboxDropdown from "@/components/InboxDropdown";
import AIUsagePill from "@/components/AIUsagePill";
import HeaderRegimeChip from "@/components/HeaderRegimeChip";
import AccountSwitcher, { ReadOnlyBanner } from "@/components/AccountSwitcher";
import ExecutionSourceChip, { ExecutionSourceBanner } from "@/components/ExecutionSourceChip";
import { syncExecutionSourceToBroker, BROKER_CHANGED_EVENT } from "@/lib/executionSource";
import { useConfirm } from "@/components/ConfirmDialog";

const linkCls = ({ isActive }) =>
  `flex items-center gap-2.5 px-3 h-9 text-sm border-l-2 transition-colors duration-150 ${
    isActive
      ? "border-l-white text-foreground bg-secondary/40"
      : "border-l-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/25"
  }`;

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const [killSwitch, setKillSwitch] = useState(user?.workspace?.kill_switch_active || false);
  const [brokerMode, setBrokerMode] = useState("disconnected");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const refreshBroker = () =>
      api.get("/broker/status")
        .then((r) => {
          setBrokerMode(r.data.mode);
          // Auto-follow: if the user hasn't explicitly picked an
          // execution source, sync it to the connected broker so
          // analytics always target the account they're actually
          // trading against.
          syncExecutionSourceToBroker(r.data.mode);
        })
        .catch(() => {});
    refreshBroker();
    // Poll every 30s so a server-side broker switch (e.g. registry
    // activate) reflects in the chip without a manual page refresh.
    const iv = setInterval(refreshBroker, 30_000);
    const handler = (e) => {
      if (e.detail?.mode) {
        setBrokerMode(e.detail.mode);
        syncExecutionSourceToBroker(e.detail.mode);
      } else {
        refreshBroker();
      }
    };
    window.addEventListener(BROKER_CHANGED_EVENT, handler);
    return () => {
      clearInterval(iv);
      window.removeEventListener(BROKER_CHANGED_EVENT, handler);
    };
  }, []);

  // Close mobile nav whenever the route changes.
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const activateKill = async () => {
    const ok = await confirm.confirm({
      variant: "kill",
      title: "Activate kill switch?",
      description: "All new orders will be blocked workspace-wide until an owner deactivates it with password re-verification.",
      confirmLabel: "Activate Kill Switch",
      testId: "kill-switch-activate-confirm",
    });
    if (!ok) return;
    await api.post("/workspace/kill-switch/activate");
    setKillSwitch(true);
    toast.error("Kill switch ACTIVATED — all new orders blocked.", { duration: 4000 });
  };
  const deactivateKill = async () => {
    const pw = await confirm.promptPassword({
      variant: "risk",
      title: "Deactivate kill switch",
      description: "Confirm your password to re-enable order submission.",
      placeholder: "Password",
      confirmLabel: "Deactivate",
      testId: "kill-switch-deactivate-prompt",
    });
    if (!pw) return;
    try {
      await api.post("/workspace/kill-switch/deactivate", { password: pw });
      setKillSwitch(false);
      toast.success("Kill switch deactivated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to deactivate");
    }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Mobile nav overlay */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 bg-background/70 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
          data-testid="mobile-nav-scrim"
        />
      )}

      {/* Sidebar — hidden below md, slide-in as drawer when open */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-56 shrink-0 border-r border-border/60 flex flex-col bg-card transition-transform duration-200 ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        data-testid="app-sidebar"
        aria-hidden={typeof window !== "undefined" && window.innerWidth < 768 && !mobileNavOpen}
      >
        <div className="px-4 h-12 flex items-center gap-2 border-b border-border/60">
          <img src="/wingwise-mark.svg" alt="" className="w-5 h-5" aria-hidden="true" />
          <div className="font-semibold tracking-tight">Wing<span className="text-[#0094FF]">Wise</span></div>
          <div className="ml-auto text-[10px] mono text-muted-foreground uppercase tracking-wider">MVP</div>
          <button
            onClick={() => setMobileNavOpen(false)}
            className="md:hidden ml-2 p-1 -mr-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current"
            aria-label="Close navigation"
            data-testid="mobile-nav-close"
          >
            <X size={14}/>
          </button>
        </div>
        <nav className="py-2 flex flex-col">
          <NavLink to="/opportunities" className={linkCls} data-testid="nav-opportunities">
            <Sparkles size={16} strokeWidth={1.5}/> Opportunities
          </NavLink>
          <NavLink to="/scanner" className={linkCls} data-testid="nav-scanner">
            <Radar size={16} strokeWidth={1.5}/> Scanner
          </NavLink>
          <NavLink to="/dashboard" className={linkCls} data-testid={NAV.dashboard}>
            <LayoutGrid size={16} strokeWidth={1.5} /> Dashboard
          </NavLink>
          <NavLink to="/portfolios" className={linkCls} data-testid="nav-portfolios">
            <PieChart size={16} strokeWidth={1.5} /> Portfolios
          </NavLink>
          <NavLink to="/positions" className={linkCls} data-testid={NAV.positions}>
            <TrendingUp size={16} strokeWidth={1.5} /> Positions
          </NavLink>
          <NavLink to="/activity" className={linkCls} data-testid="nav-activity">
            <Radar size={16} strokeWidth={1.5} /> Activity
          </NavLink>
          <NavLink to="/journal" className={linkCls} data-testid={NAV.journal}>
            <BookOpen size={16} strokeWidth={1.5} /> Journal
          </NavLink>
          <NavLink to="/analysis" className={linkCls} data-testid="nav-analysis-history">
            <History size={16} strokeWidth={1.5} /> Analysis History
          </NavLink>
          <NavLink to="/performance" className={linkCls} data-testid="nav-performance">
            <Activity size={16} strokeWidth={1.5} /> Performance Lab
          </NavLink>
          <NavLink to="/ai-quality" className={linkCls} data-testid="nav-ai-quality">
            <Brain size={16} strokeWidth={1.5} /> AI Quality
          </NavLink>
          <NavLink to="/catalysts" className={linkCls} data-testid="nav-catalysts">
            <Radar size={16} strokeWidth={1.5} /> Catalysts
          </NavLink>
          <NavLink to="/scanner-policy" className={linkCls} data-testid="nav-scanner-policy">
            <Brain size={16} strokeWidth={1.5} /> Scanner Policy
          </NavLink>
          <NavLink to="/learning" className={linkCls} data-testid={NAV.learning}>
            <GraduationCap size={16} strokeWidth={1.5} /> Learning
          </NavLink>
          <NavLink to="/audit" className={linkCls} data-testid={NAV.audit}>
            <ScrollText size={16} strokeWidth={1.5} /> Audit
          </NavLink>
          <NavLink to="/system" className={linkCls} data-testid="nav-system">
            <Activity size={16} strokeWidth={1.5} /> System
          </NavLink>
          <NavLink to="/settings" className={linkCls} data-testid={NAV.settings}>
            <Settings size={16} strokeWidth={1.5} /> Settings
          </NavLink>
        </nav>

        <div className="mt-auto p-3 border-t border-border/60 space-y-2">
          <div className="text-[10px] mono uppercase tracking-wider text-muted-foreground">Broker</div>
          <div className="flex items-center gap-2">
            <span className={`w-1.5 h-1.5 ${brokerMode === "alpaca_paper" ? "bg-signal-green" : brokerMode === "mock" ? "bg-signal-yellow" : "bg-muted-foreground"}`} />
            <span className="text-xs mono uppercase">{brokerMode.replace("_", " ")}</span>
          </div>

          <div className="text-[10px] mono uppercase tracking-wider text-muted-foreground pt-2">Session</div>
          <div className="text-xs truncate">{user?.email}</div>
          <div className="text-[10px] mono uppercase text-muted-foreground">{user?.role} · {user?.workspace?.name}</div>
          <button className="btn-ghost w-full !justify-start !px-0" onClick={logout} data-testid={AUTH.logoutBtn}>
            <LogOut size={14} strokeWidth={1.5} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-12 border-b border-border/60 bg-card/60 backdrop-blur px-3 sm:px-4 flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-1 -ml-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current"
            aria-label="Open navigation"
            data-testid="mobile-nav-open"
          >
            <Menu size={18}/>
          </button>
          <div className="hidden sm:block text-xs mono uppercase text-muted-foreground tracking-wider truncate">
            {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · US Equities · Paper Only
          </div>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            <ExecutionSourceChip compact/>
            <HeaderRegimeChip/>
            <div className="hidden sm:block"><AIUsagePill/></div>
            <AccountSwitcher/>
            <InboxDropdown/>
            {killSwitch ? (
              <button className="btn-danger !px-2 sm:!px-3" onClick={deactivateKill} data-testid={NAV.killSwitchDeactivate}>
                <ShieldAlert size={14} strokeWidth={1.75}/> <span className="hidden sm:inline">KILL SWITCH ACTIVE</span><span className="sm:hidden">KILL</span>
              </button>
            ) : (
              <button className="btn-outline !px-2 sm:!px-3" onClick={activateKill} data-testid={NAV.killSwitchActivate}>
                <Zap size={14} strokeWidth={1.75}/> <span className="hidden sm:inline">Kill Switch</span>
              </button>
            )}
          </div>
        </header>

        {killSwitch && (
          <div className="bg-[#FF3B30]/10 border-b border-[#FF3B30]/40 px-4 py-2 text-sm text-[#FF3B30] mono" data-testid={NAV.killSwitchBanner}>
            KILL SWITCH ACTIVE — All new order submissions are blocked workspace-wide.
          </div>
        )}

        <ReadOnlyBanner/>
        <ExecutionSourceBanner/>

        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
